/**
 * Condition evaluator — evaluates a Condition DSL node against a context.
 * Uses key-presence detection (no `op` discriminator).
 */

import type { Condition, FieldCondition } from '../types/conditions';

export interface ConditionContext {
  /** Current field value being tested (for value-level operators like equals, isEmpty) */
  fieldValue?: unknown;
  /** All form values (for cross-field conditions) */
  values: Record<string, unknown>;
  /** Previous form values snapshot — used by `changed` condition to detect value diffs */
  previousValues?: Record<string, unknown>;
  /** Runtime meta context */
  meta: Record<string, unknown>;
  /** Resolve option prop from a select field's selected option */
  getOptionProp?: (fieldName: string, prop: string) => unknown;
  /** Custom condition resolvers */
  customResolvers?: Record<string, (context: ConditionContext) => boolean>;
}

export function evaluateCondition(
  condition: Condition,
  context: ConditionContext,
): boolean {
  const c = condition as unknown as Record<string, unknown>;

  // ─── Changed condition: { field: 'x', changed: true } — must precede generic field dispatch ─
  if ('changed' in c && 'field' in c) {
    const currentVal = getNestedValue(context.values, c.field as string);
    const prevVal = context.previousValues
      ? getNestedValue(context.previousValues, c.field as string)
      : undefined;
    return currentVal !== prevVal;
  }

  // ─── Field condition: { field: '{ns}.x', equals/notEquals/... } ─
  if ('field' in c) {
    return evaluateFieldCondition(condition as FieldCondition, context);
  }

  // ─── Logical combiners ─
  if ('allOf' in c) {
    return (c.allOf as Condition[]).every((child) => evaluateCondition(child, context));
  }
  if ('anyOf' in c) {
    return (c.anyOf as Condition[]).some((child) => evaluateCondition(child, context));
  }
  if ('not' in c) {
    return !evaluateCondition(c.not as Condition, context);
  }

  // ─── Specialty: optionProp ─
  if ('optionProp' in c) {
    const propValue = context.getOptionProp?.(c.optionProp as string, c.prop as string);
    if (c.notEquals !== undefined) {
      return propValue !== c.notEquals;
    }
    if (c.equals !== undefined) {
      return propValue === c.equals;
    }
    return Boolean(propValue);
  }

  // ─── Specialty: meta ─
  if ('meta' in c) {
    const metaVal = getNestedValue(context.meta, c.meta as string);
    if (c.equals !== undefined) {
      return metaVal === c.equals;
    }
    if (c.notEquals !== undefined) {
      return metaVal !== c.notEquals;
    }
    if (c.in !== undefined) {
      return (c.in as unknown[]).includes(metaVal);
    }
    return !isEmpty(metaVal);
  }

  // ─── Specialty: custom ─
  if ('custom' in c) {
    const resolver = context.customResolvers?.[c.custom as string];
    if (!resolver) {
      console.warn(`[form-engine] Unknown custom condition: ${c.custom}`);
      return false;
    }
    return resolver(context);
  }

  // ─── Value-level conditions (operate on context.fieldValue) ─
  if ('notEquals' in c) return context.fieldValue !== c.notEquals;
  if ('equals' in c) return context.fieldValue === c.equals;
  if ('isEmpty' in c) return isEmpty(context.fieldValue);
  if ('notEmpty' in c) return !isEmpty(context.fieldValue);
  if ('gt' in c) return compareNumeric(context.fieldValue, c.gt as number, (a, b) => a > b);
  if ('gte' in c) return compareNumeric(context.fieldValue, c.gte as number, (a, b) => a >= b);
  if ('lt' in c) return compareNumeric(context.fieldValue, c.lt as number, (a, b) => a < b);
  if ('lte' in c) return compareNumeric(context.fieldValue, c.lte as number, (a, b) => a <= b);
  if ('in' in c) return (c.in as unknown[]).includes(context.fieldValue);
  if ('matches' in c) {
    if (typeof context.fieldValue !== 'string') return false;
    try {
      return new RegExp(c.matches as string).test(context.fieldValue);
    } catch {
      return false;
    }
  }

  console.warn('[form-engine] Unknown condition shape:', condition);
  return false;
}

function evaluateFieldCondition(condition: FieldCondition, context: ConditionContext): boolean {
  const fieldVal = getNestedValue(context.values, condition.field);

  if (condition.notEquals !== undefined) return fieldVal !== condition.notEquals;
  if (condition.equals !== undefined) return fieldVal === condition.equals;
  if (condition.isEmpty) return isEmpty(fieldVal);
  if (condition.notEmpty) return !isEmpty(fieldVal);
  if (condition.gt !== undefined) return compareNumeric(fieldVal, condition.gt, (a, b) => a > b);
  if (condition.gte !== undefined) return compareNumeric(fieldVal, condition.gte, (a, b) => a >= b);
  if (condition.lt !== undefined) return compareNumeric(fieldVal, condition.lt, (a, b) => a < b);
  if (condition.lte !== undefined) return compareNumeric(fieldVal, condition.lte, (a, b) => a <= b);
  if (condition.in) return condition.in.includes(fieldVal);
  if (condition.contains !== undefined) return arrayContains(fieldVal, condition.contains);
  if (condition.notContains !== undefined) return !arrayContains(fieldVal, condition.notContains);
  if (condition.matches !== undefined) {
    if (typeof fieldVal !== 'string') return false;
    try {
      return new RegExp(condition.matches).test(fieldVal);
    } catch {
      return false;
    }
  }

  // No comparison key set — default to "field is not empty"
  return !isEmpty(fieldVal);
}

function arrayContains(fieldVal: unknown, searchValue: unknown): boolean {
  if (Array.isArray(fieldVal)) {
    return fieldVal.some((item) => {
      if (typeof item === 'object' && item != null && 'label' in item) {
        return (item as { label: string }).label === searchValue;
      }
      return item === searchValue;
    });
  }
  if (typeof fieldVal === 'string') {
    return fieldVal.includes(String(searchValue));
  }
  return false;
}

function compareNumeric(
  value: unknown,
  threshold: number,
  compare: (value: number, threshold: number) => boolean,
): boolean {
  const num = Number(value);
  if (Number.isNaN(num)) return false;
  return compare(num, threshold);
}

function isEmpty(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  const result = path.split('.').reduce<unknown>((acc, key) => {
    if (acc == null || typeof acc !== 'object') return undefined;
    // Support array.length and array[n] access so conditions like
    // { field: "items.length", equals: 1 } or { field: "items.0.name" } work.
    if (Array.isArray(acc)) {
      if (key === 'length') return acc.length;
      const idx = Number(key);
      return Number.isInteger(idx) ? acc[idx] : undefined;
    }
    return (acc as Record<string, unknown>)[key];
  }, obj);
  return result;
}
